// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import i18n from 'app/common/lib/i18n';
import locationUpsell from 'app/common/images/web_nux_location_upsell.png';
import NuxCountryList from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxCountryList';
import NuxLanguageList from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxLanguageList';
import PropTypes from 'prop-types';
import storage from 'app/common/lib/Storage';
import type { Dispatch } from 'redux';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import setNuxProgress, { type NuxStep } from 'app/common/redux/actions/nuxProgress';
import {
  DEFAULT_COUNTRY,
  DEFAULT_LOCALE,
  NUX_COUNTRY_LOCALE_STEP_STATUS,
  SHOWED,
  COMPLETED,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Button, Heading, Image, Text } from 'gestalt';
import { updateUser, setUserState } from 'app/common/redux/actions/nux/updateUser';

type OwnProps = {|
  showTableOfContents: boolean,
  toggleLoadingIndicator: (on: boolean) => void,
  incrementNuxStep: () => void,
  isBizNux: boolean,
  isRenux: boolean,
  personalizationCopy: {
    headerText: string,
  },
  requestContext: RequestContextType,
|};

type DispatchProps = {|
  updateUserInfo: ({
    updateUserData: {
      country: ?string,
      locale: ?string,
    },
  }) => Promise<any>,
  setNuxLocaleProgress: (number) => void,
  dispatchSetNuxStepProgress: (NuxStep) => void,
|};

type StateProps = {|
  reduxCountry: string,
  reduxLocale: string,
|};

type Props = {| ...OwnProps, ...StateProps, ...DispatchProps |};

type State = {
  locale: string,
  country: string,
  swappingLanguage: boolean,
  swappingCountry: boolean,
};

type Context = {
  country: string,
  locale: string,
};

const DEFAULT_COUNTRY_SELECTOR = 'Country';

export class NuxLocaleCountryStep extends PureComponent<Props, State> {
  state = {
    locale: this.props.reduxLocale || (this.context || {}).locale || DEFAULT_LOCALE,
    country: this.props.reduxCountry || (this.context || {}).country || DEFAULT_COUNTRY,
    swappingLanguage: false,
    swappingCountry: false,
  };

  inNuxUsLocationUpsellExp: boolean;

  componentDidMount() {
    const {
      dispatchSetNuxStepProgress,
      isRenux,
      requestContext,
      setNuxLocaleProgress,
    } = this.props;
    if (!isRenux) {
      setNuxLocaleProgress(SHOWED);
    }

    dispatchSetNuxStepProgress('country');

    // Experiment to test web desktop nux upsell
    if (new SingleTreatmentExp(requestContext, 'web_nux_us_location_upsell').enabled()) {
      this.inNuxUsLocationUpsellExp = true;
      // set default country to be default option instead of user's IP country
      this.setState({ country: DEFAULT_COUNTRY_SELECTOR });
      // Upsell user to share location
      this.onLocationButtonOnMount();
    }

    const stepSeen =
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_SIGNUP_USER_LOCALE) ||
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_SIGNUP_USER_COUNTRY);
    if (!stepSeen) {
      track(PCONST.thrift.EventType.VIEW, {
        view: PCONST.thrift.ViewType.ORIENTATION,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_LOCALE_COUNTRY_STEP,
      });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { toggleLoadingIndicator, isBizNux, updateUserInfo } = this.props;
    const { country: prevCountry, locale: prevLocale } = prevState;
    const { country, locale } = this.state;
    const updatedCountry = prevCountry !== country;
    const updatedLocale = prevLocale !== locale;

    if (updatedCountry || updatedLocale) {
      updateUserInfo({ updateUserData: { country, locale } }).then(() => {
        if (updatedCountry) {
          storage.localStorage.setItem(PCONST.LOCALSTORAGE_SIGNUP_USER_COUNTRY, country);
          this.setState({ swappingCountry: true });
        }
        if (updatedLocale) {
          storage.localStorage.setItem(PCONST.LOCALSTORAGE_SIGNUP_USER_LOCALE, locale);
          if (!isBizNux) {
            toggleLoadingIndicator(true);
          } else {
            this.setState({ swappingLanguage: true });
          }

          changeWindowLocation('/');
        }
      });
    }
  }

  onNextButtonClick = () => {
    const { incrementNuxStep, isRenux, updateUserInfo, setNuxLocaleProgress } = this.props;
    const { country, locale } = this.state;
    updateUserInfo({ updateUserData: { country, locale } }).then(() => {
      if (!isRenux) {
        setNuxLocaleProgress(COMPLETED);
      }

      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.BROWSER,
        element: PCONST.thrift.ElementType.LOCALE_COUNTRY_NEXT_BUTTON,
        view: PCONST.thrift.ViewType.ORIENTATION,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_LOCALE_COUNTRY_STEP,
      });
      incrementNuxStep();
    });
  };

  onLocationButtonOnMount = () => {
    // Official documentation and support: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/geolocation
    if (!navigator.geolocation) {
      // Geolocation is not supported by your browser
      return;
    }
    const success = (position) => {
      // user accepted permission
      this.setState({
        country: this.props.reduxCountry || (this.context || {}).country || DEFAULT_COUNTRY,
      });
      // mapping mobile-centric location context log to web, using while in use
      track(PCONST.thrift.EventType.LOCATION_PERMISSIONS_ACCEPT_WHILE_IN_USE);
    };
    const error = () => {
      // user denied permission
      track(PCONST.thrift.EventType.LOCATION_PERMISSIONS_DENY);
    };
    navigator.geolocation.getCurrentPosition(success, error);
  };

  context: Context;

  updateLocale = ({ value: locale }: { value: string }) => this.setState({ locale });
  updateCountry = ({ value: country }: { value: string }) => this.setState({ country });

  render() {
    const {
      personalizationCopy: { headerText },
    } = this.props;
    const { country, locale, swappingLanguage, swappingCountry } = this.state;

    // collect location experiment
    let finalCountry = country;
    if (this.inNuxUsLocationUpsellExp && !swappingCountry) {
      // disable button and pass current value for setting default country
      finalCountry = DEFAULT_COUNTRY_SELECTOR;
    }

    return (
      <Box display="flex" direction="column" height="100%" padding={8} width="100%">
        <Box
          alignItems="center"
          dangerouslySetInlineStyle={
            //overrides left-justified multiline headings
            { __style: { textAlign: 'center' } }
          }
          display="flex"
          direction="column"
          flex="grow"
          justifyContent="around"
          marginBottom={8}
        >
          <Box width="100%">
            <Heading size="sm">{headerText}</Heading>
          </Box>
          <Experiment name="web_nux_us_location_upsell" activate={false}>
            {({ group }) => {
              let marginBottom = -12;
              let marginTop = -12;
              // different layout values for image variant due to additional image in flex positioned elements
              if (group.includes('enabled_image')) {
                marginBottom = 7;
                marginTop = 6;
              }
              return group.includes('enabled') ? (
                <Box marginBottom={marginBottom} marginTop={marginTop}>
                  <Text>
                    {i18n._(
                      "We'll use this to personalize the Pins and ads you see",
                      'location upsell supporting text',
                    )}
                  </Text>
                </Box>
              ) : null;
            }}
          </Experiment>
          <Box width="100%">
            <Box data-test-id="nux-locale-select">
              <NuxLanguageList
                id="newUserLanguage"
                name="locale"
                onChange={this.updateLocale}
                value={locale}
              />
            </Box>
            <Box marginTop={4} data-test-id="nux-country-select">
              <NuxCountryList
                id="newUserCountry"
                name="country"
                onChange={this.updateCountry}
                value={finalCountry}
              />
            </Box>
          </Box>
        </Box>
        <Box bottom data-test-id="nux-locale-country-next-btn">
          <Button
            size="lg"
            color="red"
            text={i18n._('Next', 'go to next step')}
            onClick={this.onNextButtonClick}
            type="submit"
            disabled={swappingLanguage || finalCountry === DEFAULT_COUNTRY_SELECTOR}
          />
        </Box>
        <Experiment name="web_nux_us_location_upsell" activate={false}>
          {({ group }) =>
            // get experiment group name to know if image should be shown
            group.includes('image') ? (
              <Box
                marginTop={5}
                marginBottom={-8}
                marginLeft={-8}
                marginRight={-8}
                overflow="hidden"
                dangerouslySetInlineStyle={{ __style: { borderRadius: 8 } }}
              >
                <Image
                  alt={i18n._('Web Desktop Location Upsell', 'location upsell image')}
                  src={locationUpsell}
                  naturalHeight={170}
                  naturalWidth={622}
                />
              </Box>
            ) : null
          }
        </Experiment>
      </Box>
    );
  }
}

NuxLocaleCountryStep.contextTypes = {
  country: PropTypes.string,
  locale: PropTypes.string,
};

const mapStateToProps = ({ viewer }: ReduxState): StateProps => ({
  reduxCountry: viewer.country ? viewer.country : '',
  reduxLocale: viewer.locale ? viewer.locale : '',
});

const mapDispatchToProps = (dispatch: Dispatch<any>, props: OwnProps): DispatchProps => ({
  updateUserInfo: (data) => dispatch(updateUser(data)),
  setNuxLocaleProgress: (progress) =>
    dispatch(setUserState(NUX_COUNTRY_LOCALE_STEP_STATUS, progress)),
  dispatchSetNuxStepProgress: (step: NuxStep) => dispatch(setNuxProgress(step)),
});

export default compose(
  withRequestContext,
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(NuxLocaleCountryStep);
